import { useEffect } from 'react';
import { trackExperimentViewed } from '../analytics';
import {
  isSyntheticMonitor,
  markExperimentExposureTracked,
  wasExperimentExposureTracked
} from '../utils';

const FULL_PAGE_FORM_EXPERIMENT_ID = '5381471903416320';

interface OptimizelyWebExperimentState {
  experimentName?: string;
  isActive?: boolean;
  isInExperimentHoldback?: boolean;
  variation?: { id: string; name: string } | null;
}

declare global {
  interface Window {
    optimizely?: {
      get?: (name: 'state') =>
        | {
            getExperimentStates?: () => Record<
              string,
              OptimizelyWebExperimentState
            >;
          }
        | undefined;
      push?: (message: unknown) => void;
    };
  }
}

function readExperimentState(experimentId: string) {
  return globalThis.window.optimizely
    ?.get?.('state')
    ?.getExperimentStates?.()?.[experimentId];
}

// Optimizely Web (snippet), not the Feature Experimentation SDK behind OptimizelyProvider.
function useTrackOptimizelyWebExperiment(experimentId: string): void {
  useEffect(() => {
    if (isSyntheticMonitor() || wasExperimentExposureTracked(experimentId)) {
      return;
    }

    let cancelled = false;
    const track = () => {
      const state = readExperimentState(experimentId);
      if (
        cancelled ||
        !state?.isActive ||
        !state.variation ||
        state.isInExperimentHoldback ||
        wasExperimentExposureTracked(experimentId)
      ) {
        return;
      }
      markExperimentExposureTracked(experimentId);
      trackExperimentViewed({
        experiment_id: experimentId,
        variation_id: state.variation.id,
        experiment_name: state.experimentName ?? experimentId,
        variation_name: state.variation.name,
        experimentation_platform: 'Optimizely'
      });
    };

    track();
    if (wasExperimentExposureTracked(experimentId)) {
      return;
    }

    ['campaignDecided', 'pageActivated'].forEach((name) => {
      globalThis.window.optimizely?.push?.({
        type: 'addListener',
        filter: { type: 'lifecycle', name },
        handler: track
      });
    });
    return () => {
      cancelled = true;
    };
  }, [experimentId]);
}

function useTrackFullPageFormExperiment(): void {
  useTrackOptimizelyWebExperiment(FULL_PAGE_FORM_EXPERIMENT_ID);
}

export { useTrackFullPageFormExperiment };
